import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Image, Loader2, Save } from 'lucide-react';

/* ─── Rich editor for a single link (image, layout, title, CTA) ─── */
export const LinkRichEditor = ({ link, onSave, onImageUpload, onClose, isPro }) => {
  const [title, setTitle] = useState(link.title || '');
  const [description, setDescription] = useState(link.description || '');
  const [ctaText, setCtaText] = useState(link.cta_text || '');
  const [ctaColor, setCtaColor] = useState(link.cta_color || '#FFD500');
  const [layout, setLayout] = useState(link.layout || 'icon_only');
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const imgRef = useRef(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      const patch = {
        description: description.trim(),
        cta_text: ctaText.trim(),
        cta_color: ctaColor || null,
        layout: layout,
      };
      const finalTitle = (title.trim() || link.title || '').trim();
      if (finalTitle) patch.title = finalTitle;
      await onSave(link.id, patch);
      onClose();
    } catch (_) {} finally { setSaving(false); }
  };

  // Auto-save layout the moment user clicks — preview reflects the new look
  // immediately, no Save-button round-trip required.
  const pickLayout = async (newLayout) => {
    if (newLayout === layout) return;
    setLayout(newLayout);
    try { await onSave(link.id, { layout: newLayout }); }
    catch (_) {}
  };

  const handleImg = async (file) => {
    if (!file) return;
    setUploadingImg(true);
    try { await onImageUpload(link.id, file); }
    catch (_) {} finally { setUploadingImg(false); }
  };

  // Layout options. Pro-gated ones show a lock if user isn't Pro.
  const layouts = [
    { id: 'icon_only', name: 'Icon only', sub: 'Small icon in the top row', pro: false },
    { id: 'icon_in_button', name: 'Icon in button', sub: 'Linktree-style pill below', pro: true },
    { id: 'both', name: 'Both', sub: 'Top icon + pill below', pro: true },
  ];

  return (
    <div className="mt-1 pl-8 pt-3 border-t border-border space-y-3" data-testid={`link-rich-editor-${link.id}`}>
      <div className="flex items-center gap-3">
        {link.image_url ? (
          <img src={link.image_url} alt="" className="w-14 h-14 rounded-lg object-cover" />
        ) : (
          <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center">
            <Image className="w-5 h-5 text-muted-foreground" />
          </div>
        )}
        <Button variant="outline" size="sm" type="button" disabled={uploadingImg} onClick={() => imgRef.current?.click()} data-testid={`link-image-upload-${link.id}`}>
          {uploadingImg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Image className="w-4 h-4 mr-2" />}
          {link.image_url ? 'Change image' : 'Upload image'}
        </Button>
        <input ref={imgRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden"
          onChange={(e) => handleImg(e.target.files?.[0])} />
      </div>

      {/* Layout picker */}
      <div>
        <Label className="text-xs">How this link appears</Label>
        <div className="grid grid-cols-3 gap-2 mt-1">
          {layouts.map(opt => {
            const locked = opt.pro && !isPro;
            const selected = layout === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                disabled={locked}
                onClick={() => pickLayout(opt.id)}
                className={`relative text-left p-2.5 rounded-lg border-2 transition-all ${
                  selected
                    ? 'border-primary bg-primary/5'
                    : locked
                      ? 'border-border opacity-50 cursor-not-allowed'
                      : 'border-border hover:border-muted-foreground/40'
                }`}
                data-testid={`link-layout-${opt.id}-${link.id}`}
              >
                <p className="text-xs font-semibold flex items-center gap-1.5">
                  {opt.name}
                  {opt.pro && (
                    <span className="text-[9px] uppercase tracking-wider text-emerald-600 font-bold">Pro</span>
                  )}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{opt.sub}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <Label className="text-xs">Title</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-9 text-sm mt-1" maxLength={80} data-testid={`link-title-${link.id}`} />
      </div>
      <div>
        <Label className="text-xs">Description <span className="text-muted-foreground">(optional)</span></Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value.slice(0, 300))} className="text-sm mt-1 min-h-[60px]" data-testid={`link-description-${link.id}`} />
      </div>
      {/* Button text + color only apply to the legacy rich-card variant
          (image-based links with no explicit new-layout pick) */}
      {layout === 'icon_only' && (link.image_url || ['custom','website','email','phone','sms','other'].includes(link.platform)) && (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Button text</Label>
          <Input value={ctaText} onChange={(e) => setCtaText(e.target.value.slice(0, 40))} className="h-9 text-sm mt-1" placeholder="Click Me!" data-testid={`link-cta-text-${link.id}`} />
        </div>
        <div>
          <Label className="text-xs">Button color</Label>
          <div className="flex items-center gap-2 mt-1">
            <input type="color" value={ctaColor} onChange={(e) => setCtaColor(e.target.value)} className="h-9 w-12 rounded cursor-pointer border" data-testid={`link-cta-color-${link.id}`} />
            <Input value={ctaColor} onChange={(e) => setCtaColor(e.target.value)} className="h-9 text-sm font-mono flex-1" />
          </div>
        </div>
      </div>
      )}
      <Button onClick={handleSave} disabled={saving} size="sm" className="w-full" data-testid={`save-link-${link.id}`}>
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Save changes
      </Button>
    </div>
  );
};
